// ============================================
// SELECT2 BRIDGE - Alpine.js Component
// Bidirectional sync between Select2 and Alpine.js x-model
// ============================================

import Alpine from 'alpinejs';

// Declare jQuery as global
declare const jQuery: any;

interface Select2Option {
    id: string | number;
    text: string;
    selected?: boolean;
}

interface Select2Options {
    width?: string;
    placeholder?: string;
    minimumResultsForSearch?: number;
    dropdownParent?: string;
    allowClear?: boolean;
    multiple?: boolean;
    data?: Select2Option[]; // Dynamic options
}

interface Select2BridgeConfig {
    modelBind: string;
    disabledBind?: string; // Name of parent property to bind disabled state
    dataBind?: string; // Name of parent property to bind options data (reactive)
    data?: Select2Option[]; // Static initial options data (only used if dataBind is not set)
    options?: Select2Options;
    onChange?: (value: any) => void;
}

export function registerSelect2Bridge() {
    Alpine.data('select2', (config: Select2BridgeConfig = { modelBind: 'value' }) => ({
        value: null as any,
        modelBind: config.modelBind,
        select2Instance: null as any,
        isInitialized: false,
        wrapper: null as HTMLElement | null,
        // The options select2 holds now (as JSON), so the same list is not rebuilt.
        currentData: '',
        destroyed: false,

        // Helper to get nested property value
        getNestedValue(obj: any, path: string): any {
            return path.split('.').reduce((current, key) => current?.[key], obj);
        },

        // Helper to set nested property value
        setNestedValue(obj: any, path: string, value: any): void {
            const keys = path.split('.');
            const lastKey = keys.pop()!;
            const target = keys.reduce((current, key) => current[key], obj);
            if (target) {
                target[lastKey] = value;
            }
        },

        init() {
            // Read initial value from parent using Alpine.js property access
            this.value = this.getNestedValue(this, this.modelBind) ?? null;

            // Wrap select element in a container for skeleton support
            // Note: <select> elements don't support ::before, so we need a wrapper
            this.wrapSelectElement();

            // Wait for Alpine to finish rendering (important for x-for options)
            this.$nextTick(() => {
                this.initializeSelect2();
            });
        },

        updateDisabledState(isDisabled: boolean) {
            if (typeof jQuery === 'undefined') return;

            const $ = jQuery as any;
            const $element = $(this.$el);

            if (this.isInitialized) {
                // Toggle disabled on Select2
                $element.prop('disabled', isDisabled);
                $element.trigger('change.select2');
            }

            // Toggle disabled class on wrapper for visual feedback
            if (this.wrapper) {
                if (isDisabled) {
                    this.wrapper.classList.add('disabled');
                } else {
                    this.wrapper.classList.remove('disabled');
                }
            }
        },

        wrapSelectElement() {
            const select = this.$el as HTMLSelectElement;

            // Create wrapper div
            const wrapper = document.createElement('div');
            wrapper.className = 'select2-wrapper';

            // Copy width-related classes from select to wrapper for proper skeleton sizing
            const selectClasses = select.className.split(' ');
            const widthClasses = selectClasses.filter(cls =>
                cls.startsWith('w-') || cls.startsWith('min-w-') || cls.startsWith('max-w-')
            );
            if (widthClasses.length > 0) {
                wrapper.className = 'select2-wrapper ' + widthClasses.join(' ');
            }

            // Insert wrapper before select and move select inside
            select.parentNode?.insertBefore(wrapper, select);
            wrapper.appendChild(select);

            this.wrapper = wrapper;
        },

        initializeSelect2() {
            // Check if jQuery and Select2 are available
            if (typeof jQuery === 'undefined' || typeof (jQuery as any).fn.select2 === 'undefined') {
                console.warn('Select2 not available, falling back to native select');
                return;
            }

            const $ = jQuery as any;
            const $element = $(this.$el);

            // Default Select2 options
            const defaultOptions: Select2Options = {
                width: '100%', // Use CSS width
                minimumResultsForSearch: Infinity, // Disable search by default
            };

            // Merge with user options
            const select2Options: any = {
                ...defaultOptions,
                ...config.options,
            };

            // Load initial data with priority: dataBind > data
            if (config.dataBind) {
                // Use reactive data from parent property
                const initialData = this.getNestedValue(this, config.dataBind);
                if (initialData && initialData.length > 0) {
                    select2Options.data = initialData;
                }
            } else if (config.data && config.data.length > 0) {
                // Use static data from config
                select2Options.data = config.data;
            }

            // Initialize Select2
            this.currentData = JSON.stringify(select2Options.data || []);
            $element.select2(select2Options);
            this.select2Instance = $element;
            this.isInitialized = true;

            // Add class to wrapper to hide skeleton
            if (this.wrapper) {
                this.wrapper.classList.add('select2-ready');
            }

            // Set initial value from x-model
            if (this.value !== null && this.value !== undefined) {
                $element.val(this.value).trigger('change.select2');
            }

            // Listen to Select2 changes and update Alpine x-model
            $element.on('select2:select select2:unselect', (e: any) => {
                const newValue = $element.val();

                // Use helper to set nested value
                this.setNestedValue(this, this.modelBind, newValue);

                // Call user's onChange callback if provided
                if (config.onChange) {
                    config.onChange(newValue);
                }
            });

            // Watch for changes in parent property and sync to local value
            this.$watch(this.modelBind, (newValue: any) => {
                if (this.value != newValue) {
                    this.value = newValue;
                    // Set Select2 value
                    if (this.isInitialized) {
                        $element.val(newValue).trigger('change.select2');
                        // trigger change event to notify Select2
                        $element.trigger('change');
                    }
                }
            });

            // Watch for disabled state changes from parent
            if (config.disabledBind) {
                // Set initial disabled state
                const initialDisabled = this.getNestedValue(this, config.disabledBind) ?? false;
                this.updateDisabledState(initialDisabled);

                // Watch for changes
                this.$watch(config.disabledBind, (isDisabled: boolean) => {
                    this.updateDisabledState(isDisabled);
                });
            }

            // Watch for data changes from parent
            if (config.dataBind) {
                // Watch for changes in data
                this.$watch(config.dataBind, (newData: Select2Option[]) => {
                    this.updateOptions(newData);
                });
            }
        },

        updateOptions(options: Select2Option[]) {
            if (!this.isInitialized || this.destroyed || typeof jQuery === 'undefined') return;

            const $ = jQuery as any;
            const $element = $(this.$el);

            // The watcher runs queued: an x-if may have taken the select out (and undone select2) by then.
            if (!$element.hasClass('select2-hidden-accessible')) return;

            // A getter that rebuilds the same list (a reload of the data behind it) changes nothing:
            // rebuilding would only close a dropdown the user has open.
            const json = JSON.stringify(options || []);
            if (json === this.currentData) return;
            this.currentData = json;

            // Destroy Select2 instance
            $element.select2('destroy');

            // Clear existing options
            $element.empty();

            // Re-initialize with new data
            const select2Options: any = {
                width: '100%',
                minimumResultsForSearch: Infinity,
                ...config.options,
                data: options || []
            };

            $element.select2(select2Options);

            // Restore value if exists
            if (this.value !== null && this.value !== undefined) {
                $element.val(this.value).trigger('change.select2');
            }
        },

        destroy() {
            // Watchers already queued must not touch it any more.
            this.destroyed = true;

            // Clean up Select2 when component is destroyed
            if (this.select2Instance && typeof (jQuery as any).fn.select2 !== 'undefined') {
                try {
                    this.select2Instance.select2('destroy');
                } catch (e) {
                    console.warn('Error destroying Select2:', e);
                }
            }

            // Remove wrapper
            if (this.wrapper && this.wrapper.parentNode) {
                const select = this.$el;
                this.wrapper.parentNode.insertBefore(select, this.wrapper);
                this.wrapper.parentNode.removeChild(this.wrapper);
            }
        },
    }));
}
